import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Trash2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  new: { label: "New", cls: "bg-gold/15 text-gold" },
  contacted: { label: "Contacted", cls: "bg-brand-green/10 text-brand-green" },
  closed: { label: "Closed", cls: "bg-sage/15 text-sage" },
};
const NEXT = { new: "contacted", contacted: "closed", closed: "new" };

export default function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [enquiries, setEnquiries] = useState(null);

  useEffect(() => {
    if (location.state?.user) return;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data))
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [location.state, navigate]);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/enquiries`, { withCredentials: true })
      .then((res) => setEnquiries(res.data))
      .catch(() => toast.error("Could not load enquiries"));
  }, [user]);

  const advanceStatus = async (enquiry) => {
    try {
      const { data } = await axios.patch(
        `${API}/enquiries/${enquiry.id}`,
        { status: NEXT[enquiry.status || "new"] },
        { withCredentials: true }
      );
      setEnquiries((list) => list.map((e) => (e.id === enquiry.id ? data : e)));
    } catch {
      toast.error("Status update failed");
    }
  };

  const remove = async (enquiry) => {
    if (!window.confirm(`Delete enquiry from ${enquiry.full_name}?`)) return;
    try {
      await axios.delete(`${API}/enquiries/${enquiry.id}`, { withCredentials: true });
      setEnquiries((list) => list.filter((e) => e.id !== enquiry.id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    navigate("/admin/login", { replace: true });
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory" data-testid="admin-loading">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-sage">Verifying session…</p>
      </div>
    );
  }

  const counts = { new: 0, contacted: 0, closed: 0 };
  (enquiries || []).forEach((e) => { counts[e.status || "new"] += 1; });

  return (
    <div className="min-h-screen bg-ivory font-sans text-ink" data-testid="admin-dashboard">
      <header className="border-b border-sand bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <a href="/" className="font-serif text-2xl font-semibold text-brand-green">
            OravioCare
            <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.25em] text-sage">Leads</span>
          </a>
          <div className="flex items-center gap-4">
            <div className="hidden items-center gap-2.5 sm:flex" data-testid="admin-user-chip">
              {user.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full" referrerPolicy="no-referrer" />}
              <span className="text-sm font-medium text-ash">{user.name || user.email}</span>
            </div>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-sand px-4 py-2 text-xs font-semibold text-ash transition-colors duration-300 hover:border-brand-green hover:text-brand-green"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            ["New enquiries", counts.new, "text-gold"],
            ["Contacted", counts.contacted, "text-brand-green"],
            ["Closed", counts.closed, "text-sage"],
          ].map(([label, n, cls]) => (
            <div key={label} className="rounded-2xl border border-sand bg-white p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
              <p className={`font-serif text-4xl font-semibold ${cls}`}>{n}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-sage">{label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 overflow-x-auto rounded-3xl border border-sand bg-white shadow-[0_20px_60px_rgba(8,61,52,0.06)]">
          <table className="w-full min-w-[900px] text-left" data-testid="admin-enquiries-table">
            <thead>
              <tr className="border-b border-sand font-mono text-[10px] uppercase tracking-[0.2em] text-sage">
                <th className="px-6 py-4 font-medium">Patient</th>
                <th className="px-6 py-4 font-medium">Treatment</th>
                <th className="px-6 py-4 font-medium">Country</th>
                <th className="px-6 py-4 font-medium">Urgency</th>
                <th className="px-6 py-4 font-medium">Est. savings</th>
                <th className="px-6 py-4 font-medium">Received</th>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4" />
              </tr>
            </thead>
            <tbody>
              {(enquiries || []).map((e) => {
                const st = STATUS[e.status || "new"];
                return (
                  <tr key={e.id} data-testid={`admin-row-${e.id.slice(0, 8)}`} className="border-b border-cream last:border-0 hover:bg-cream/60">
                    <td className="px-6 py-4">
                      <p className="text-sm font-semibold text-ink">{e.full_name}</p>
                      <p className="mt-0.5 text-xs text-sage">{e.email}{e.phone ? ` · ${e.phone}` : ""}</p>
                      {e.notes && <p className="mt-1 max-w-xs truncate text-xs italic text-sage">“{e.notes}”</p>}
                    </td>
                    <td className="px-6 py-4 text-sm text-ash">{e.treatment}</td>
                    <td className="px-6 py-4 text-sm text-ash">{e.country}</td>
                    <td className="px-6 py-4 font-mono text-xs text-ash">{e.urgency}</td>
                    <td className="px-6 py-4 text-sm font-medium text-brand-green">{e.estimated_savings || "—"}</td>
                    <td className="px-6 py-4 font-mono text-xs text-sage">
                      {new Date(e.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        data-testid={`status-btn-${e.id.slice(0, 8)}`}
                        onClick={() => advanceStatus(e)}
                        title="Click to advance status"
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider transition-transform duration-200 hover:scale-105 ${st.cls}`}
                      >
                        {st.label} <ArrowRight size={11} />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        data-testid={`delete-btn-${e.id.slice(0, 8)}`}
                        onClick={() => remove(e)}
                        className="text-sage transition-colors duration-300 hover:text-red-600"
                        aria-label="Delete enquiry"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {enquiries && enquiries.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-14 text-center text-sm text-sage" data-testid="admin-empty-state">
                    No enquiries yet — new quote requests will appear here.
                  </td>
                </tr>
              )}
              {!enquiries && (
                <tr>
                  <td colSpan={8} className="px-6 py-14 text-center font-mono text-xs uppercase tracking-[0.2em] text-sage">
                    Loading enquiries…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
